import { getSessionStore } from "app/utils/sessionStore";
import ReactECharts from "echarts-for-react";
import { getThemeColors, getUSANumberFormat } from "app/utils/utils";
import { THEME } from "app/utils/types";
import { getChartLabels } from "lib/utils";
import { getTooltipLabel } from "../../Common/ChartFilter";
import { RadioButton } from "primereact/radiobutton";
import { useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { useIPContext } from "../../InteractiveProposalProvider";

export interface PremiumFinanceChartsDataProps {
  roi: number;
  year: number;
  planYear: number;
  gain: number;
  runningBalance: number;
  financialImpact: number;
}

const getTooltipContent = (
  params: any,
  chartsData: PremiumFinanceChartsDataProps[],
  color: string,
  dividerColor: string,
  selectedChartFilter: string,
) => {
  if (!params || params.length === 0) return "";

  const dataIndex = params[0]?.dataIndex ?? 0;
  const point = chartsData?.[dataIndex] || {};
  const title = params[0]?.axisValueLabel ?? params[0]?.axisValue ?? "";

  const gain = getUSANumberFormat(point?.gain || 0, false);
  const roiPct = getUSANumberFormat(Math.round(point?.roi ?? 0));
  const hiddenDelta = getUSANumberFormat(point?.runningBalance || 0, false);
  const impact = getUSANumberFormat(point?.financialImpact || 0, false);
  const tooltipLabel = getTooltipLabel(selectedChartFilter);
  return `
    <div style="padding:10px 12px; min-width: 220px;">
      <div style="font-weight:600; margin-bottom:6px; color:${color};">${tooltipLabel} ${title}</div>
      <div style="display:flex; justify-content:space-between; margin:4px 0;">
        <span style="color:${color};">Gain</span>
        <span style="color:${color};">$${gain}</span>
      </div>
      <div style="display:flex; justify-content:space-between; margin:4px 0;">
        <span style="color:${color};">ROI%</span>
        <span style="color:${color};">${roiPct}%</span>
      </div>
      <div style="height:1px; background:${dividerColor}; margin:8px 0;"></div>
      <div style="display:flex; justify-content:space-between; margin:4px 0;">
        <span style="color:${color};">Hidden Delta</span>
        <span style="color:${color};">$${hiddenDelta}</span>
      </div>
      <div style="display:flex; justify-content:space-between; margin:4px 0;">
        <span style="color:${color};">Financial Impact</span>
        <span style="color:${color};">$${impact}</span>
      </div>
    </div>`;
};

const PFChart = ({
  chartsData,
  selectedChartFilter,
  selectedSeriesRef,
  zoomStateRef,
}: {
  chartsData: PremiumFinanceChartsDataProps[];
  selectedChartFilter: string;
  selectedSeriesRef: React.MutableRefObject<string | null>;
  zoomStateRef: React.MutableRefObject<{ start: number; end: number } | null>;
}) => {
  const {
    selectedGrowthRate,
    setSelectedGrowthRate,
    selectedChartFilterType,
    setSelectedChartFilterType,
  } = useIPContext();

  const globalTheme = getSessionStore("globalTheme");
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor } = getThemeColors(branding);
  const color = globalTheme === THEME.DARK ? "#ffffff" : "#333333";
  const dividerColor = globalTheme === THEME.DARK ? "#3A3A50" : "#e0e0e0";
  const growthRateOptions = useMemo(
    () =>
      Array.from({ length: 21 }, (_, idx) => 5 + idx).map((rate) => ({
        label: `${rate}%`,
        value: rate,
      })),
    [],
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex gap-2 mb-2 justify-between">
        <div className="flex gap-2">
          <div className="flex align-items-center">
            <RadioButton
              inputId="Hidden Delta"
              name="financial-impact"
              value="runningBalance"
              onChange={(e) => setSelectedChartFilterType(e.value)}
              checked={selectedChartFilterType === "runningBalance"}
            />
            <label htmlFor="Hidden Delta" className="ml-2">
              Hidden Delta
            </label>
          </div>
          <div className="flex align-items-center">
            <RadioButton
              inputId="Gain"
              name="financial-impact"
              value="gain"
              onChange={(e) => setSelectedChartFilterType(e.value)}
              checked={selectedChartFilterType === "gain"}
            />
            <label htmlFor="Gain" className="ml-2">
              Net Death Benefit
            </label>
          </div>
        </div>
        {selectedChartFilterType === "runningBalance" && (
          <div className="flex align-items-center small-selector">
            <p className="mr-2 text-sm">% Growth</p>
            <Dropdown
              inputId="growth-rate"
              value={selectedGrowthRate}
              onChange={(e) => setSelectedGrowthRate(e.value)}
              options={growthRateOptions}
              optionLabel="label"
              placeholder="% Growth"
              className="w-[120px] text-sm"
            />
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0">
        <ReactECharts
          onEvents={{
            click: (params: any) => {
              if (selectedSeriesRef) {
                selectedSeriesRef.current = params?.seriesName || null;
              }
            },
            mouseover: (params: any) => {
              if (selectedSeriesRef) {
                selectedSeriesRef.current = params?.seriesName || null;
              }
            },
            mouseout: () => {
              if (selectedSeriesRef) {
                selectedSeriesRef.current = null;
              }
            },
            dataZoom: (params: any) => {
              if (zoomStateRef) {
                zoomStateRef.current = { start: params.start, end: params.end };
              }
            },
          }}
          option={{
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: "line",
              },
              backgroundColor: globalTheme === THEME.DARK ? "#2A2A3D" : "#ffffff",
              borderColor: globalTheme === THEME.DARK ? "#3A3A50" : "#e0e0e0",
              borderWidth: 1,
              borderRadius: 8,
              padding: 0,
              textStyle: {
                color,
                fontSize: 12,
              },
              formatter(params: any) {
                return getTooltipContent(
                  params,
                  chartsData,
                  color,
                  dividerColor,
                  selectedChartFilter,
                );
              },
              extraCssText: `box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border: none !important;`,
            },
            legend: {
              show: false,
            },
            grid: {
              left: "3%",
              right: "7%",
              bottom: "25%",
              top: "5%",
              containLabel: true,
              show: false,
            },
            dataZoom: [
              {
                type: "slider",
                show: true,
                start: zoomStateRef.current?.start || 0,
                end:
                  zoomStateRef.current?.end || Math.min(100, (9 / (chartsData?.length || 1)) * 100),
                minValueSpan: 1,
                maxValueSpan: chartsData?.length || 1,
                height: 20,
                bottom: 10,
                borderColor: "transparent",
                fillerColor: "rgba(167,183,204,0.4)",
                textStyle: {
                  color: `${color}`,
                },
                preventDefaultMouseMove: false,
                throttle: 0,
                rangeMode: ["percent", "percent"],
                brushSelect: false,
              },
            ],
            xAxis: {
              type: "category",
              boundaryGap: false,
              data: getChartLabels(chartsData, selectedChartFilter),
              axisLabel: {
                color: `${color}`,
                fontFamily: "Conthrax Semi-Bold",
                fontSize: 10,
              },
            },
            yAxis: {
              type: "value",
              axisLabel: { show: false }, // hide y-axis labels
              axisLine: { show: false }, // hide y-axis line
              splitLine: { show: false }, // optional: hide grid lines
              max(value: any) {
                return Math.round(value.max * 1.2);
              },
              min(value: any) {
                const paddedMin = Math.floor(value.min * 1.2);
                return Math.min(0, paddedMin);
              },
              boundaryGap: [0, "100%"],
            },
            series: [
              {
                name: "",
                type: "line",
                data: chartsData?.map((item: any) =>
                  selectedChartFilterType === "runningBalance"
                    ? item?.runningBalance ?? 0
                    : item?.gain ?? 0,
                ),
                smooth: true,
                showSymbol: false,
                lineStyle: {
                  color: primaryColor,
                  width: 2,
                },
                itemStyle: {
                  color: primaryColor,
                },
                areaStyle: {
                  color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      {
                        offset: 0,
                        color: `${primaryColor.substring(0, 7)}`, // Semi-transparent at top
                      },
                      {
                        offset: 0.5,
                        color: `${primaryColor.substring(0, 7)}70`, // Medium transparency in middle
                      },
                      {
                        offset: 1,
                        color: `${primaryColor.substring(0, 7)}00`, // Transparent at bottom
                      },
                    ],
                  },
                },
              },
            ],
          }}
          style={{ width: "100%", height: "100%", margin: 0, padding: 0 }}
        />
      </div>
    </div>
  );
};

export default PFChart;
